/**
 * @file 成长中心AI助手通知
 * @author izzieyspu
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Frame, Card, Titlebar, Text, Flex } from '@tencent/odc';
import Icon from '@tencent/adq-icons';
import { Button } from '@tencent/spaui';
import dayjs from 'dayjs';

import { hasFeatureFlag } from '@/services';
import { saveCombinedValue, getFromCombinedValue } from '@/utils/storage';
import { TrackType } from '@/pages/agency/growup/dashboard/model';
import { scientific } from '@/utils/formater';

import { getSummaryReport } from '../../api/growup';
import useAIAssistant from '../../hooks/use-ai-assistant';
import { AGENT_ID, AI_ASSISTANT_REPORT_EVENT_NAME, FEATURE_TYPE } from '../../constants';
import { GrowupReportType, GROWUP_REPORT_NAME_MAP, GROWUP_REPORT_TYPE } from './constants';

interface GrowupNoticeProps {
  type: GrowupReportType;
  track: TrackType;
  name: string;
}

const GROWUP_REPORT_SHOW_KEY = 'ams_growup_report_show';

const GrowupNotice = (props: GrowupNoticeProps) => {
  const { type, track, name } = props;
  const [data, setData] = useState<any>({});
  const [show, setShow] = useState(false);

  // 计算数据日期和对比日期
  const { dataDate, compareDate } = useMemo(() => {
    if (!hasFeatureFlag('is_enable_ai_assistent') || !type) {
      return {};
    }
    // 数据日期为上周/月最后一天
    const dataDate = dayjs().subtract(1, type)
      .endOf(type)
      .format('YYYYMMDD');
    // 对比日期为上上周/月最后一天
    const compareDate = dayjs().subtract(2, type)
      .endOf(type)
      .format('YYYYMMDD');
    // 周报
    if (type === GROWUP_REPORT_TYPE.WEEK) {
      return { dataDate, compareDate };
    }
    // 月报
    if (type === GROWUP_REPORT_TYPE.MONTH) {
      const quarterStartDate = dayjs(dataDate).startOf('quarter')
        .format('YYYYMMDD');
      // 如果跨季度，对比日期为本月第一天
      if (compareDate < quarterStartDate) {
        const monthStartDate = dayjs(dataDate).startOf('month')
          .format('YYYYMMDD');
        return { dataDate, compareDate: monthStartDate };
      }
      return { dataDate, compareDate };
    }
    return {};
  }, [type]);

  const { showAIAssistant, aiAssistantModal } = useAIAssistant();

  // 关闭通知
  const handleClose = useCallback(() => {
    setShow(false);
    saveCombinedValue(GROWUP_REPORT_SHOW_KEY, track, dataDate);
  }, [track, dataDate]);

  // 唤起AI助手
  const handleAIAssistantShow = useCallback((content?: string) => {
    showAIAssistant({
      defaultAgentId: AGENT_ID.GROWUP,
      ...(content ? {
        defaultPrompt: content,
      } : {
        defaultFeatureType: FEATURE_TYPE.GROWUP_REPORT,
      }),
    });
    handleClose();
  }, [showAIAssistant, track, handleClose]);

  // 获取周/月报
  useEffect(() => {
    setShow(false);
    if (!dataDate || !compareDate) {
      return;
    }
    getSummaryReport({
      track,
      partition_time: dataDate,
      compare_time: compareDate,
    }).then((res: any) => {
      if (res?.code === 0) {
        const { partition_time } = res?.data || {};
        if (partition_time) {
          // 未展示过当前AI报告时展示
          const lastShowDate = getFromCombinedValue(GROWUP_REPORT_SHOW_KEY, track);
          if (!lastShowDate || lastShowDate < partition_time) {
            setData(res?.data || {});
            setShow(true);
          }
        }
      }
    });
  }, [track, dataDate, compareDate]);

  if (!hasFeatureFlag('is_enable_ai_assistent') || !type) {
    return null;
  }

  return <>
    {aiAssistantModal}
    {show && <Frame
      position="fixed"
      z={9999}
      right={16}
      bottom={16}
    >
      <Card
        bordered
        radius={8}
        padding='0'
        bg="white"
        minWidth={320}
        headerPadding="8 0"
        hideHeaderDivider={true}
        header={<Titlebar
          size={5}
          title={<Flex itemsCenter gap={8}>
            <Icon name='magic' color="#fa8e00" style={{ color: '#fa8e00' }} />
            <Text type="strong">成长{GROWUP_REPORT_NAME_MAP[type]}报已生成</Text>
          </Flex>}
          right={<Button
            type="default"
            borderless
            size="small"
            icon={<Icon name='cancel' />}
            style={{ background: 'none' }}
            onClick={handleClose}
          />}
        />}
        bodyPadding="16"
        hideFooterDivider={true}
        footerPadding="12-16"
        footer={<Flex column gap={8}>
          <Button
            displayType="primary"
            radius={4}
            onClick={() => handleAIAssistantShow(`帮我获取${name}上${GROWUP_REPORT_NAME_MAP[type]}数据报告`)}
            data-report={`${AI_ASSISTANT_REPORT_EVENT_NAME.GROWUP_NOTICE_REPORT}__${track}`}
            data-report-extra={`relation_key:${track}`}
          >立即查看AI{GROWUP_REPORT_NAME_MAP[type]}报</Button>
          <Button
            displayType="text"
            borderless
            radius={4}
            className="inline-block"
            onClick={() => handleAIAssistantShow()}
            data-report={`${AI_ASSISTANT_REPORT_EVENT_NAME.GROWUP_NOTICE_CUSTOMIZE}__${track}`}
            data-report-extra={`relation_key:${track}`}
          >选择其他时间</Button>
        </Flex>}
      >
        <Text maxWidth={280} inlineBlock>
          您当前总分<Text type="strong" className="tw-ml-[4px]">{scientific(data.total_score, 1)}</Text>，
          {!data.total_score_wow ? '无变化' : <>{data.total_score_wow > 0 ? '上升' : '下降'}<Text type="strong" className="tw-ml-[4px] tw-mr-[4px]" color={data.total_score_wow < 0 ? '#e63d2e' : ''}>{scientific(Math.abs(data.total_score_wow), 1)}</Text>分</>}，
          排名<Text type="strong" className="tw-ml-[4px]">{data.track_rank || '-'}</Text>，
          {!data.track_rank_wow ? '无变化' : <>{data.track_rank_wow > 0 ? '上升' : '下降'}<Text type="strong" className="tw-ml-[4px] tw-mr-[4px]" color={data.track_rank_wow < 0 ? '#e63d2e' : ''}>{Math.abs(data.track_rank_wow)}</Text>名</>}，
          目前<Text type="strong" className="tw-ml-[4px]">{data.tier || '-'}</Text>。更多分析详见报告。
        </Text>
      </Card>
    </Frame>}
  </>;
};

export default GrowupNotice;
